import { relewiseRecommendations } from '@/shared/constants/constants';

const SEARCH_REGISTRY_KEY = 'relewiseUISearchTargetedConfigurations';

type RegistryKey =
  | typeof SEARCH_REGISTRY_KEY
  | typeof relewiseRecommendations.REGISTRY_WINDOW_KEY;

type RegistryState = {
  callbacks: Set<() => void>;
  installed: boolean;
};

const registries = new Map<RegistryKey, RegistryState>();

const runCallback = (callback: () => void) => {
  try {
    callback();
  } catch (e) {
    console.error('Error registering Relewise config:', e);
  }
};

const getState = (key: RegistryKey): RegistryState => {
  let state = registries.get(key);
  if (!state) {
    state = { callbacks: new Set(), installed: false };
    registries.set(key, state);
  }
  return state;
};

const installAccessor = (key: RegistryKey, state: RegistryState) => {
  if (state.installed) return;
  state.installed = true;

  let registry = window[key];

  Object.defineProperty(window, key, {
    configurable: true,
    enumerable: true,
    get: () => registry,
    set: (value: unknown) => {
      registry = value;
      if (value !== undefined) {
        state.callbacks.forEach(runCallback);
      }
    }
  });
};

const onRegistry = (key: RegistryKey, callback: () => void): void => {
  const state = getState(key);
  state.callbacks.add(callback);
  installAccessor(key, state);

  if (window[key] !== undefined) {
    runCallback(callback);
  }
};

/**
 * The Relewise app fires its first search right after creating the search
 * target registry, so registering our facets and filters at that exact moment
 * is the only way to beat it. Module scripts run before DOMContentLoaded,
 * where the app initialises, so the accessor is always in place first.
 * It re-fires on every assignment because the theme editor re-initialises.
 *
 * Note: the app's own `searchConfig` setting also registers the 'search'
 * target and would clash with ours if someone configures it.
 */
export const onRelewiseSearchRegistry = (callback: () => void): void => {
  onRegistry(SEARCH_REGISTRY_KEY, callback);
};

export const onRelewiseRecommendationRegistry = (
  callback: () => void
): void => {
  onRegistry(relewiseRecommendations.REGISTRY_WINDOW_KEY, callback);
};
